import { useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/shared/lib/supabase'
import { useLocalizedNavigate } from '@/shared/hooks'
import type { PrecadrageSubmitData } from './use-precadrage'

/**
 * Hook for creating a project when user is already authenticated
 * Unlike useSubmitPrecadrage, this creates the project directly without auth redirect
 */
export function useCreateProject() {
  const navigate = useLocalizedNavigate()
  const { t } = useTranslation()
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createProject = async (data: PrecadrageSubmitData) => {
    setIsCreating(true)
    setError(null)

    try {
      // Get current user's organization
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, organization_id')
        .single()

      if (userError || !userData?.organization_id) {
        throw new Error('Failed to get user organization')
      }

      const { organization_id: organizationId, id: userId } = userData

      // Create team (pending until payment is made)
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .insert({
          organization_id: organizationId,
          name: data.projectName,
          type: 'project',
          status: 'pending',
        })
        .select('id')
        .single()

      if (teamError || !teamData) {
        throw new Error('Failed to create team')
      }

      // Add user to the new team
      const { error: userTeamError } = await supabase.from('user_teams').insert({
        user_id: userId,
        team_id: teamData.id,
      })

      if (userTeamError) {
        console.error('Failed to add user to team:', userTeamError)
        // Non-blocking - continue anyway
      }

      // Create project with pre-scoping data
      // CADRAGE-V2-001: Create directly in 'pending' status, no manual validation
      // Project transitions to 'scoping' after payment via webhook
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .insert({
          team_id: teamData.id,
          name: data.projectName,
          description: data.description,
          status: 'pending',
          is_active: true,
          is_validated: true, // Auto-validated, ready for payment
          objective: data.objective,
          target_users: data.targetUsers,
          existing_assets: data.existingAssets,
          constraints: data.constraints || null,
          inspirations: data.inspirations || null,
        })
        .select('id, short_id')
        .single()

      if (projectError || !projectData) {
        throw new Error('Failed to create project')
      }

      // Upload attachments if any
      if (data.attachments.length > 0) {
        for (const file of data.attachments) {
          const filePath = `${projectData.id}/${Date.now()}-${file.name}`
          await supabase.storage
            .from('project-attachments')
            .upload(filePath, file, { cacheControl: '3600', upsert: false })
        }
      }

      // Show success toast
      toast.success(t('precadrage.projectCreated'))

      // CADRAGE-V2-001: Redirect to project page (which shows payment prompt)
      navigate(`/p/${projectData.short_id}`, { replace: true })
    } catch (err) {
      console.error('Error creating project:', err)
      setError(err instanceof Error ? err.message : 'An error occurred')
      setIsCreating(false)
    }
  }

  return { createProject, isCreating, error }
}
